import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  fetchFiles, 
  renameDocument as renameDocumentApi, 
  deleteDocument as deleteDocumentApi, 
  deleteVersion as deleteVersionApi, 
  moveDocument as moveDocumentApi, 
  ProviderErrorResponse, 
  type IDocument, 
  type IFilesResponse
} from '@apis/documents';
import { documentFileIds, evictDocumentBlobs, moveDocumentBlobs } from './useDocumentBlob';
import { useRefreshAfterWrites } from './useRefreshAfterWrites';

/** Stable empty list, so memos keyed on the documents don't rerun while loading. */
const NO_DOCUMENTS: IDocument[] = [];

/**
 * Files for the currently selected folder, fetched on demand. Disabled (and
 * empty) when no folder is selected — DocumentsPage prompts the user to pick one.
 *
 * The query cache is the only copy of each folder's list: a rename, move or
 * delete edits the cached list of the document's own folder at once, and puts
 * it back if the upstream write fails — whichever folder is on screen by then.
 */
export function useFiles(
  siteId: string | undefined,
  folderId: string | null,
  onError?: (op: 'rename' | 'delete' | 'deleteVersion' | 'move', error: unknown) => void,
) {
  const queryClient = useQueryClient();
  const enabled = !!siteId && !!folderId;
  const keyOf = (folder: string | null) => ['documents', 'files', siteId ?? null, folder];
  const { data, isPending, isError, error } = useQuery({
    queryKey: keyOf(folderId),
    queryFn: ({ signal }) => fetchFiles(siteId!, folderId!, signal),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: (failureCount, err) => (err instanceof ProviderErrorResponse ? false : failureCount < 2),
  });
  const documents = data?.documents ?? NO_DOCUMENTS;

  // Edits a folder's cached list, on screen or not. A refetch in flight is
  // stopped first: when it landed it would overwrite the edit.
  async function edit(folder: string, change: (docs: IDocument[]) => IDocument[]) {
    await queryClient.cancelQueries({ queryKey: keyOf(folder) });
    queryClient.setQueryData<IFilesResponse>(keyOf(folder), (old) => old && { documents: change(old.documents) });
  }

  const beginWrite = useRefreshAfterWrites();

  // Shows the new name at once, marked as saving; the folder's refetch then
  // brings the file's new (path-based) id and drops the mark.
  function renameDoc(id: string, newName: string) {
    const doc = documents.find((d) => d.id === id);
    if (doc) void persistRename(doc, newName);
  }

  async function persistRename(doc: IDocument, newName: string) {
    const settled = beginWrite(keyOf(doc.folderId));
    await edit(doc.folderId, (docs) => docs.map((d) => d.id === doc.id ? { ...d, name: newName, saving: true } : d));
    try {
      await renameDocumentApi(siteId!, doc.id, newName);
    } catch (err) {
      await edit(doc.folderId, (docs) => docs.map((d) => d.id === doc.id ? doc : d));
      void settled();
      onError?.('rename', err);
      return;
    }
    // A tracked document's rename only changes its name in the index: its files
    // and their ids stay, and so does what is cached under them.
    if (doc.documentId) { void settled(); return; }
    // A file only the store knows is renamed there, so it comes back under a new
    // id — read from the refetch, its cache carried over (same bytes).
    await settled();
    const renamed = queryClient.getQueryData<IFilesResponse>(keyOf(doc.folderId))?.documents
      .find((d) => !d.documentId && d.name === newName);
    if (renamed) moveDocumentBlobs(queryClient, siteId!, doc.id, renamed.id);
    else evictDocumentBlobs(queryClient, siteId!, [doc.id]);
  }

  // Removes the row at once, restoring it if the delete fails. Returns the
  // in-flight delete so the confirm modal can show a loading state.
  function deleteDoc(id: string): Promise<void> | void {
    const doc = documents.find((d) => d.id === id);
    if (doc) return persistDelete(doc);
  }

  async function persistDelete(doc: IDocument) {
    const settled = beginWrite(keyOf(doc.folderId));
    await edit(doc.folderId, (docs) => docs.filter((d) => d.id !== doc.id));
    try {
      await deleteDocumentApi(siteId!, doc.id);
      evictDocumentBlobs(queryClient, siteId!, documentFileIds(doc));
    } catch (err) {
      await edit(doc.folderId, (docs) => [...docs, doc]);
      onError?.('delete', err);
    } finally {
      void settled();
    }
  }

  // Deletes one version and reports what selection-following needs: the deleted
  // fileId, and the file the row presents afterwards (null = document gone).
  // Undefined = nothing happened. The new id is READ from the refetch, never
  // predicted — which version a row presents is the server's call.
  async function deleteVersion(
    documentId: string,
    seq: number,
  ): Promise<{ deletedFileId: string; presentedFileId: string | null } | undefined> {
    const doc = documents.find((d) => d.documentId === documentId);
    const deleted = doc?.versions.find((v) => v.version === seq);
    if (!doc || !deleted) return undefined;
    try {
      await deleteVersionApi(siteId!, documentId, seq);
    } catch (err) {
      onError?.('deleteVersion', err);
      return undefined;
    }
    evictDocumentBlobs(queryClient, siteId!, [deleted.fileId]);
    const key = keyOf(doc.folderId);
    await queryClient.invalidateQueries({ queryKey: key });
    const refreshed = queryClient.getQueryData<IFilesResponse>(key);
    const row = refreshed?.documents.find((d) => d.documentId === documentId);
    return { deletedFileId: deleted.fileId, presentedFileId: row?.id ?? null };
  }

  // Removes the row from its folder at once, then persists. The target folder
  // is refetched so the document shows up there (with its re-keyed id — the
  // backend moves every version file behind it).
  function moveDoc(id: string, newFolderId: string) {
    const doc = documents.find((d) => d.id === id);
    if (doc) void persistMove(doc, newFolderId);
  }

  async function persistMove(doc: IDocument, targetFolderId: string) {
    const settled = beginWrite(keyOf(doc.folderId));
    await edit(doc.folderId, (docs) => docs.filter((d) => d.id !== doc.id));
    try {
      await moveDocumentApi(siteId!, doc.id, targetFolderId);
      // Every version file moves behind the document, so all of its old
      // (path-based) ids are freed for something else to take.
      evictDocumentBlobs(queryClient, siteId!, documentFileIds(doc));
      void queryClient.invalidateQueries({ queryKey: keyOf(targetFolderId) });
    } catch (err) {
      await edit(doc.folderId, (docs) => [...docs, doc]);
      onError?.('move', err);
    } finally {
      void settled();
    }
  }

  return {
    documents,
    isPending: enabled && isPending,
    isError,
    providerError: error instanceof ProviderErrorResponse ? error : null,
    renameDoc,
    deleteDoc,
    deleteVersion,
    moveDoc,
  };
}